import type { Locale, SiteContent } from "../types";
import { zh } from "./zh";
import { en } from "./en";

/** 语言 -> 内容 的映射。新增语言在此登记。 */
const registry: Record<Locale, SiteContent> = { zh, en };

export const SUPPORTED_LOCALES = Object.keys(registry) as Locale[];
export const DEFAULT_LOCALE: Locale = "zh";

const STORAGE_KEY = "dohorizon:locale";

/**
 * 加载指定语言的站点内容。
 *
 * 目前从本地静态模块读取；将来接 CMS / 后端 API 时，
 * 只需把函数体换成 `return fetch(...).then(r => r.json())`，
 * 组件与调用方（main.ts）无需改动 —— 这正是内容与渲染分离的意义。
 */
export async function loadContent(locale: Locale): Promise<SiteContent> {
  return registry[locale] ?? registry[DEFAULT_LOCALE];
}

/** 判断字符串是否为受支持的语言 */
export function isLocale(value: string | null): value is Locale {
  return value != null && (SUPPORTED_LOCALES as string[]).includes(value);
}

/** 读取用户偏好语言：localStorage > 浏览器语言 > 默认 */
export function resolveInitialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* localStorage 不可用时降级到浏览器语言 */
  }
  const nav = typeof navigator !== "undefined" ? navigator.language.toLowerCase() : "";
  if (nav.startsWith("zh")) return "zh";
  if (nav.startsWith("en")) return "en";
  return DEFAULT_LOCALE;
}

/** 持久化用户选择的语言 */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* 忽略隐私模式等写入失败 */
  }
}
